import React from "react";
import TextInputField from "../common/TextInputField";
import CustomAutoComplete from "../common/CustomAutoComplete";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { getProviderLabel } from "../../store/helper";

// Provider options offered in the picker: value (slug) -> display label.
const PROVIDER_OPTIONS: { value: string; label: string }[] = [
  { value: "quest", label: "Quest Diagnostics" },
];

// A panel's provider may be stored as a slug ("quest"), a numeric id (7) or a
// numeric string ("7") depending on whether it was created here or returned by
// the backend. Delegate to the shared provider map so the picker resolves every
// shape — matching what the Panel Details view shows.
export const getProviderValue = (
  provider: string | number | null
): string | null => {
  return getProviderLabel(provider) || null;
};

export const getProviderKey = (label: string | null): string | null => {
  if (!label) return null;
  const found = PROVIDER_OPTIONS.find((p) => p.label === label);
  return found ? found.value : null;
};

/** True when `name` matches another saved panel's name (case-insensitive). */
const isDuplicatePanelName = (
  name: string,
  existingPanels: { id?: string | null; name?: string | null }[],
  currentPanelId: string | null
): boolean => {
  const wanted = name.trim().toLowerCase();
  return existingPanels.some(
    (panel) =>
      panel.id !== currentPanelId &&
      String(panel.name ?? "").trim().toLowerCase() === wanted
  );
};

export const validateStep1 = (
  name: string,
  provider: string | null,
  existingPanels: { id?: string | null; name?: string | null }[] = [],
  currentPanelId: string | null = null
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!name || name.trim() === "") {
    errors.push("Panel name is required");
  } else if (isDuplicatePanelName(name, existingPanels, currentPanelId)) {
    errors.push("A panel with this name already exists");
  }

  if (!provider) {
    errors.push("Provider is required");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

interface LabBuilderStep1Props {
  name: string;
  setName: (name: string) => void;
  provider: string | null;
  setProvider: (provider: string | null) => void;
}

const LabBuilderStep1: React.FC<LabBuilderStep1Props> = ({
  name,
  setName,
  provider,
  setProvider,
}) => {
  return (
    <ScrollableWrapper>
      <div style={styles.formContainer}>
        <TextInputField
          label="Panel name"
          value={name}
          handleChange={(e) => setName(e.target.value)}
          name="textInput"
        />
        <CustomAutoComplete
          label="Provider"
          options={PROVIDER_OPTIONS.map((p) => p.label)}
          value={getProviderValue(provider)}
          onChange={(value) => setProvider(getProviderKey(value))}
        />
      </div>
    </ScrollableWrapper>
  );
};

export default LabBuilderStep1;

const styles = {
  formContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  },
};
